/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useFlag, useUnleashContext } from "@unleash/proxy-client-react";
import { IRootState } from "./config/reducers";
import { getCsrfToken, getFeatures } from "./pages/login/ducks/operations";
import { SideBar } from "./parts";
import Header from "./parts/Header";
import { LayoutAction } from "./reducers/slices/layoutSlice";
import { sidebarAction } from "./reducers/slices/sidebarSlice";
import { AppRoutes, LoginRoutes } from "./routes";
import "@advapacs/common/src/App.scss";
import "./services/i18n";
import store, { useAppDispatch } from "./config/store";
import { formatRegionUrl, getTypeDomain } from "./utils/utils";
import { setUpAxiosInstance } from "./config/axios-region";
import "intro.js/introjs.css";
import "./index.scss";
import InstructionStartedIcon from "@src/components/Instruction/InstructionStartedIcon";
import AdvaViewIntroModal from "@src/components/AdvaViewIntro/AdvaViewIntroModal";
import {
  MAX_WIDTH_MOBILE,
  MAX_WIDTH_TABLET,
  NavigationInterceptor,
  setSentryApplicationContext,
  setSentryTags,
  NetworkStatusAlert,
} from "@advapacs/common";
import OauthCallback from "./components/OauthCallback";
import {
  setFaroEnabled,
  setFaroInfoLogEnabled,
} from "./services/initializeFaro";

function App() {
  const enabledFaroInfoLog = useFlag("faro-info-log");
  const enabledAdvapacsViewerInCp = useFlag("cp-advapacs-viewer");
  const enabledAdvapacsViewerIntroduce = useFlag(
    "cp-advapacs-viewer-introduce"
  );
  const { expand } = useSelector((state: IRootState) => state.sidebarReducer);
  const { completed } = useSelector(
    (state: IRootState) => state.instructionReducer
  );
  const { isAuthenticated, sessionHasBeenFetched, account, csrfToken } =
    useSelector((state: IRootState) => state.loginReducer);
  const axiosInstanceLoaded = useRef(false);
  const dispatch = useAppDispatch();
  const updateUnleashContext = useUnleashContext();
  const regionLocal = localStorage.getItem("region");
  const [expandSidebar, setExpandSidebar] = useState(expand);
  const { pathname } = useLocation();

  const isPublicRoutes = [
    "/OAuthCallback",
    "/Auth/EmailValidation",
    "/ResetPassword",
    "/ForgotPassword",
    "/RegisterComplete",
    "/public/terms-of-service",
  ].includes(pathname);

  // TRIGGER BUILD
  // Console.log("Trigger build here")

  useEffect(() => {
    setExpandSidebar(expand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storageFunction = (event: StorageEvent) => {
    if (event.key === "logged") {
      // dispatch(getUser());
    }

    if (event.key === "region") {
      // @ts-ignore
      setUpAxiosInstance(store, formatRegionUrl(event.newValue || null, true));
    }
  };

  const resize = () => {
    dispatch(LayoutAction.setMobile(window.innerWidth < MAX_WIDTH_TABLET));
    dispatch(LayoutAction.setMobileOnly(window.innerWidth < MAX_WIDTH_MOBILE));
  };

  useEffect(() => {
    if (csrfToken && isAuthenticated) {
      // @ts-ignore
      setUpAxiosInstance(store, formatRegionUrl(regionLocal || null, true));
      axiosInstanceLoaded.current = true;
    }
  }, [csrfToken, isAuthenticated, dispatch, regionLocal]);

  useEffect(() => {
    const shouldCallGetUser = window.location.pathname !== "/OAuthCallback";
    dispatch(getCsrfToken(shouldCallGetUser));
    setSentryApplicationContext();

    window.addEventListener("storage", storageFunction);
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("storage", storageFunction);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      updateUnleashContext({
        tenantId: account?.tenantId,
        userId: account?.id,
        sessionId: account?.sessionId,
        region: regionLocal || "global",
      });
      setSentryTags({
        tenantId: account?.tenantId,
        userId: account?.id,
        type: getTypeDomain(),
      });
      dispatch(getFeatures());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, account]);

  // ===================== ENABLE FARO LOG ====================
  useEffect(() => {
    setFaroInfoLogEnabled(enabledFaroInfoLog);
    setFaroEnabled(true);
  }, [enabledFaroInfoLog]);

  if (window.location.pathname === "/OAuthCallback") return <OauthCallback />;

  return (
    <>
      <NetworkStatusAlert />
      <div
        className={`${!expandSidebar ? " sidebar-xs sidebar-mobile-main" : ""}`}
      >
        {isAuthenticated && (
          <Header
            toggleSidebar={() => {
              dispatch(sidebarAction.toggle(!expandSidebar));
              setExpandSidebar(!expandSidebar);
            }}
          />
        )}
        <div
          className={`page-content ${
            isAuthenticated && sessionHasBeenFetched ? "authenticated" : ""
          }`}
        >
          {!isPublicRoutes && isAuthenticated && <SideBar />}
          {sessionHasBeenFetched && !isPublicRoutes && isAuthenticated && (
            <NavigationInterceptor>
              <AppRoutes />
            </NavigationInterceptor>
          )}
          {sessionHasBeenFetched && (!isAuthenticated || isPublicRoutes) && (
            <LoginRoutes />
          )}
        </div>
        {axiosInstanceLoaded.current &&
          isAuthenticated &&
          sessionHasBeenFetched &&
          account?.showOnboarding &&
          !completed && <InstructionStartedIcon />}
        {isAuthenticated &&
          sessionHasBeenFetched &&
          pathname.startsWith("/Studies") &&
          enabledAdvapacsViewerIntroduce &&
          enabledAdvapacsViewerInCp && <AdvaViewIntroModal />}
      </div>
    </>
  );
}

export default App;
